import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Package, FileText, Play, Clock, CheckCircle, AlertTriangle,
  Trash2, Loader2, Receipt, Wrench, MessageSquare, DollarSign, Pause, ChevronRight,
  Copy, Calculator, Edit3, MoreHorizontal, Upload, FileUp, Paperclip, Send, Mail, ExternalLink,
  UserPlus, CalendarPlus, ListTodo,
  Pen, Image as ImageIcon, Eye, Camera, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import DrawingModal from './DrawingModal';
import DrawingPreviewModal from './DrawingPreviewModal';
import { isWrapCategory } from '../components/wrap/constants';
import { TicketWorkflowShortcutDialog } from '../components/TicketWorkflowShortcutDialog';
import { SignatureSection } from '../components/SignatureSection';
import { SignatureActivityList } from '../components/SignatureActivityList';
import AddOrderItemMenu from '../components/orders/AddOrderItemMenu';
import SharedContextPanel from '../components/orders/SharedContextPanel';
import OrderAssetsPanel from '../components/orders/OrderAssetsPanel';
import { getAuthToken } from '../lib/authStorage';
import { useSetPageContext } from '../context/PageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' });

const STATUS_COLORS = {
  new_intake: 'bg-blue-500/15 text-blue-400 border-blue-500/30', awaiting_review: 'bg-yellow-500/15 text-yellow-400',
  in_production: 'bg-violet-500/15 text-violet-400', partially_complete: 'bg-cyan-500/15 text-cyan-400',
  ready_for_pickup: 'bg-green-500/15 text-green-400', completed: 'bg-green-600/15 text-green-600',
  on_hold: 'bg-red-500/15 text-red-400', cancelled: 'bg-slate-500/15 text-gray-500',
  approved: 'bg-teal-500/15 text-teal-400', new: 'bg-blue-500/15 text-blue-400',
  queued: 'bg-indigo-500/15 text-indigo-400', in_qc: 'bg-amber-500/15 text-amber-400',
  ready: 'bg-green-500/15 text-green-400', rework: 'bg-red-500/15 text-red-400',
  awaiting_quote: 'bg-orange-500/15 text-orange-400', quote_sent: 'bg-purple-500/15 text-purple-400',
  draft: 'bg-slate-500/15 text-gray-500', sent: 'bg-blue-500/15 text-blue-400', paid: 'bg-green-500/15 text-green-400',
  not_started: 'bg-gray-200 text-gray-700', in_progress: 'bg-violet-500/15 text-violet-400',
  paused: 'bg-orange-500/15 text-orange-400', complete: 'bg-green-500/15 text-green-400',
};
const CATEGORY_LABELS = { rigid_signs: 'Rigid Signs', banners: 'Banners', cut_vinyl: 'Cut Vinyl', digital_print: 'Digital Print', vehicle_wrap: 'Vehicle Wrap', apparel: 'Apparel', services: 'Services', promo_misc: 'Promo / Misc', custom: 'Custom' };
const PRIORITY_COLORS = { rush: 'bg-red-500 text-gray-900', urgent: 'bg-orange-500 text-gray-900', high: 'bg-amber-500/80 text-black', normal: 'bg-slate-600 text-slate-200' };
const DEPT_LABELS = { design: 'Design', print: 'Print', cut_trim: 'Cut / Trim', lamination: 'Lamination', weed_mask: 'Weed / Mask', sewing_finishing: 'Sewing', assembly: 'Assembly', apparel: 'Apparel', wrap_prep: 'Wrap Prep', install: 'Install', qc_review: 'QC', packaging: 'Packaging', delivery: 'Delivery' };
const fmt = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  // Phase 3: declare context so assistant knows "this order".
  useSetPageContext({
    page: 'order_detail',
    recordType: 'order',
    recordId: id,
    recordLabel: order?.order_number || id,
  });
  const [activities, setActivities] = useState([]);
  const [financials, setFinancials] = useState({ quotes: [], invoices: [], work_orders: [] });
  const [prodSummary, setProdSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [tab, setTab] = useState('tickets');
  const [taskLoading, setTaskLoading] = useState('');
  const [orderFiles, setOrderFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [orderFileThumbs, setOrderFileThumbs] = useState({});
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [showDrawingModal, setShowDrawingModal] = useState(false);
  const [drawingMarkupImage, setDrawingMarkupImage] = useState(null);
  const [previewDrawing, setPreviewDrawing] = useState(null);
  const [drawingThumbs, setDrawingThumbs] = useState({});
  const [employees, setEmployees] = useState([]);
  const [shortcutMode, setShortcutMode] = useState('');
  const [shortcutTicket, setShortcutTicket] = useState(null);
  const [drawingFilter, setDrawingFilter] = useState('all');
  const [previewFile, setPreviewFile] = useState(null);
  const [quickPhotoUploading, setQuickPhotoUploading] = useState(false);
  const quickPhotoCameraRef = useRef(null);
  const quickPhotoGalleryRef = useRef(null);
  const quickPhotoTicketRef = useRef(null);

  const handleQuickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setQuickPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('label', `Photo — ${file.name}`);
      const res = await axios.post(`${API}/orders/${id}/upload`, formData, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const uploadedFile = res.data;
      toast.success('Photo uploaded — opening markup');
      await load();
      // Immediately open markup with the uploaded photo
      const contentRes = await axios.get(`${API}/orders/${id}/files/${uploadedFile.id}/content`, {
        headers: hdr(),
        responseType: 'blob',
      });
      const contentUrl = URL.createObjectURL(contentRes.data);
      const ticketId = quickPhotoTicketRef.current || null;
      setDrawingMarkupImage({
        id: uploadedFile.id,
        label: uploadedFile.label || uploadedFile.filename || file.name,
        contentUrl,
      });
      setShowDrawingModal(true);
      quickPhotoTicketRef.current = null;
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setQuickPhotoUploading(false);
    }
  };

  const triggerQuickPhoto = (source = 'camera', ticketId = null) => {
    quickPhotoTicketRef.current = ticketId;
    if (source === 'camera' && quickPhotoCameraRef.current) {
      quickPhotoCameraRef.current.click();
    } else if (quickPhotoGalleryRef.current) {
      quickPhotoGalleryRef.current.click();
    }
  };

  const load = async () => {
    try {
      const [orderRes, actRes, finRes, prodRes, filesRes, drawingsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/orders/${id}`, { headers: hdr() }),
        axios.get(`${API}/orders/${id}/activity`, { headers: hdr() }),
        axios.get(`${API}/orders/${id}/financials`, { headers: hdr() }).catch(() => ({ data: { quotes: [], invoices: [], work_orders: [] } })),
        axios.get(`${API}/orders/${id}/production-summary`, { headers: hdr() }).catch(() => ({ data: null })),
        axios.get(`${API}/orders/${id}/files`, { headers: hdr() }).catch(() => ({ data: [] })),
        axios.get(`${API}/order-drawings/${id}`, { headers: hdr() }).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`, { headers: hdr() }).catch(() => ({ data: [] })),
      ]);
      setOrder(orderRes.data);
      setActivities(actRes.data);
      setFinancials(finRes.data);
      setProdSummary(prodRes.data);
      const nextOrderFiles = filesRes.data || [];
      setOrderFiles(nextOrderFiles);
      loadOrderFileThumbs(nextOrderFiles);
      setEmployees(employeesRes.data || []);
      const drawingsList = drawingsRes.data || [];
      setDrawings(drawingsList);
      // Load thumbnails
      loadDrawingThumbs(drawingsList);
    } catch { toast.error('Failed to load order'); }
    finally { setLoading(false); }
  };

  const replaceOrderFileThumbs = (nextThumbs) => {
    setOrderFileThumbs((previousThumbs) => {
      Object.values(previousThumbs || {}).forEach((thumbUrl) => {
        if (thumbUrl) URL.revokeObjectURL(thumbUrl);
      });
      return nextThumbs;
    });
  };

  const loadOrderFileThumbs = async (filesList) => {
    const imageFiles = (filesList || []).filter((file) => file.content_type?.startsWith('image/'));
    if (imageFiles.length === 0) {
      replaceOrderFileThumbs({});
      return;
    }

    const thumbEntries = await Promise.all(imageFiles.map(async (file) => {
      try {
        const contentUrl = await getFileContentUrl(file);
        return [file.id, contentUrl];
      } catch {
        return [file.id, null];
      }
    }));
    replaceOrderFileThumbs(Object.fromEntries(thumbEntries.filter(([, value]) => value)));
  };

  const loadDrawingThumbs = async (drawingsList) => {
    const thumbs = {};
    for (const d of drawingsList) {
      try {
        const res = await axios.get(`${API}/order-drawings/file/${d.id}`, {
          headers: hdr(),
          responseType: 'blob',
        });
        thumbs[d.id] = URL.createObjectURL(res.data);
      } catch {
        thumbs[d.id] = null;
      }
    }
    setDrawingThumbs(thumbs);
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => () => {
    Object.values(orderFileThumbs || {}).forEach((thumbUrl) => {
      if (thumbUrl) URL.revokeObjectURL(thumbUrl);
    });
  }, [orderFileThumbs]);

  const startProduction = async () => {
    setActionLoading('production');
    try {
      const res = await axios.post(`${API}/orders/${id}/start-production`, {}, { headers: hdr() });
      toast.success(`Production started: ${res.data.tasks_created} tasks created`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const generateDoc = async (type) => {
    setActionLoading(type);
    try {
      const res = await axios.post(`${API}/orders/${id}/generate-${type}`, {}, { headers: hdr() });
      if (type === 'work_order') {
        toast.success(`Work order generated with ${res.data.total_tickets || 0} ticket(s)`);
      } else {
        toast.success(`${fmt(type)} generated: $${(res.data.total || 0).toFixed(2)}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(''); }
  };

  // Internal production work-ticket PDF (pricing hidden by default).
  const printWorkTicket = async (withPricing = false) => {
    const key = withPricing ? 'work_ticket_pricing' : 'work_ticket';
    setActionLoading(key);
    try {
      const url = `${API}/orders/${id}/work-ticket/pdf${withPricing ? '?include_pricing=true' : ''}`;
      const res = await axios.get(url, { headers: hdr(), responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Pop-up was blocked. Allow pop-ups for this site and try again.');
      }
      // Release the object URL after the new tab has had a chance to read it.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate work ticket');
    } finally {
      setActionLoading('');
    }
  };

  const updateTask = async (taskId, status) => {
    setTaskLoading(taskId);
    try {
      await axios.put(`${API}/production-tasks/${taskId}`, { status }, { headers: hdr() });
      toast.success(`Task ${fmt(status)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setTaskLoading(''); }
  };

  const deleteOrder = async () => {
    if (!window.confirm('Delete this order and all related records?')) return;
    try {
      await axios.delete(`${API}/orders/${id}`, { headers: hdr() });
      toast.success('Order deleted');
      navigate('/orders');
    } catch { toast.error('Failed to delete'); }
  };

  const sendEmail = async (docType) => {
    setActionLoading('email');
    try {
      // Find the latest quote or invoice for this order
      const docs = docType === 'quote' ? financials.quotes : financials.invoices;
      if (!docs?.length) {
        toast.error(`No ${docType} found. Generate one first.`);
        setActionLoading('');
        return;
      }
      const doc = docs[0];
      await axios.post(`${API}/documents/send-email`, {
        document_type: docType,
        document_id: doc.id,
        customer_email: order.email,
        customer_name: order.customer_name,
        subject: `${docType === 'quote' ? 'Quote' : 'Invoice'} from ${order.company_name || 'SignGuy AI'} - ${order.order_number}`,
      }, { headers: hdr() });
      toast.success(`${docType === 'quote' ? 'Quote' : 'Invoice'} sent to ${order.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally { setActionLoading(''); }
  };

  const updateOrderStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/orders/${id}`, { status: newStatus }, { headers: hdr() });
      toast.success(`Status updated to ${fmt(newStatus)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const duplicateTicket = async (ticketId) => {
    try {
      const res = await axios.post(`${API}/job-tickets/${ticketId}/duplicate`, {}, { headers: hdr() });
      toast.success(`Duplicated → ${res.data.ticket_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to duplicate'); }
  };

  const deleteTicket = async (ticketId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this order item and its tasks?')) return;
    try {
      await axios.delete(`${API}/job-tickets/${ticketId}`, { headers: hdr() });
      toast.success('Item deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingFile(true);
    try {
      for (const f of files) {
        const formData = new FormData();
        formData.append('file', f);
        formData.append('label', f.name);
        await axios.post(`${API}/orders/${id}/upload`, formData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
      }
      toast.success(`${files.length} file(s) uploaded`);
      load();
    } catch { toast.error('Upload failed'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/orders/${id}/files/${fileId}`, { headers: hdr() });
      toast.success('File deleted');
      setOrderFiles(prev => prev.filter(f => f.id !== fileId));
    } catch { toast.error('Failed to delete file'); }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${API}/documents?filter=templates`, { headers: hdr() });
      setTemplates(res.data.filter(d => d.is_template));
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      const res = await axios.post(`${API}/documents/${template.id}/populate-from-template`, {
        customer_id: order?.customer_id,
        job_id: id
      }, { headers: hdr() });
      
      toast.success(`Created "${template.name}" with your order data!`);
      setShowTemplateSelector(false);
      
      const filesRes = await axios.get(`${API}/orders/${id}/files`, { headers: hdr() });
      setOrderFiles(filesRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to use template');
    }
  };

  const getFileContentUrl = async (file) => {
    const response = await axios.get(`${API}/orders/${id}/files/${file.id}/content`, { headers: hdr(), responseType: 'blob' });
    return URL.createObjectURL(response.data);
  };

  const sendFileForApproval = async (file) => {
    try {
      const blobRes = await axios.get(`${API}/orders/${id}/files/${file.id}/content`, { headers: hdr(), responseType: 'blob' });
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blobRes.data);
      });
      await axios.post(`${API}/approvals`, {
        customer_id: order.customer_id,
        job_id: id,
        file_url: dataUrl,
        file_name: file.filename,
        description: `Artwork approval for ${order.order_number}`,
      }, { headers: hdr() });
      toast.success('Artwork sent for approval');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send artwork for approval');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;
  if (!order) return <div className="text-center py-20 text-gray-500">Order not found</div>;

  const tickets = order.job_tickets || [];
  const getEmployeeName = (employeeId) => employees.find((employee) => employee.id === employeeId)?.name || employeeId;
  const drawingFilterOptions = [
    { id: 'all', label: 'All Drawings' },
    { id: 'order', label: 'Order Drawings' },
    { id: 'job_ticket', label: 'Item Drawings' },
    { id: 'uploaded_image', label: 'Image Markups' },
  ];
  const filteredDrawings = drawings.filter((drawing) => drawingFilter === 'all' || drawing.parent_type === drawingFilter);
  const latestOrderDraftDrawing = drawings.find((drawing) => drawing.parent_type === 'order' && drawing.status === 'draft') || null;
  // Order total: use active price from pricing snapshot, fallback to estimated_price
  const orderTotal = order.order_total || tickets.reduce((sum, t) => {
    const snapshot = t.pricing_snapshot;
    if (snapshot?.active_price) return sum + snapshot.active_price;
    return sum + (t.estimated_price || 0);
  }, 0);
  const workflowTickets = tickets.filter(t => t.production_flow_enabled);
  const allTasks = prodSummary?.tasks || [];

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}><ArrowLeft className="w-5 h-5 text-gray-500" /></Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white font-heading">{order.order_number}</h1>
              <Badge variant="outline" className={STATUS_COLORS[order.status]}>{fmt(order.status)}</Badge>
            </div>
            <p className="text-slate-400 text-sm">{order.customer_name}{order.company_name ? ` — ${order.company_name}` : ''}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                <FileText className="w-4 h-4" /> Generate
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => generateDoc('quote')} disabled={!!actionLoading || tickets.length === 0}>
                <FileText className="w-4 h-4 mr-2" /> Generate Quote
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => generateDoc('invoice')} disabled={!!actionLoading || tickets.length === 0}>
                <Receipt className="w-4 h-4 mr-2" /> Generate Invoice
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => generateDoc('work_order')} disabled={!!actionLoading || tickets.length === 0}>
                <Wrench className="w-4 h-4 mr-2" /> Generate Work Order
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => printWorkTicket(false)}
                disabled={!!actionLoading}
                data-testid="print-work-ticket-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print Work Ticket
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => printWorkTicket(true)}
                disabled={!!actionLoading}
                data-testid="print-work-ticket-with-pricing-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print Work Ticket <span className="ml-1 text-[10px] text-violet-600">+ Pricing</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                <Send className="w-4 h-4" /> Send
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => sendEmail('quote')} disabled={!!actionLoading || !order.email}>
                <Mail className="w-4 h-4 mr-2" /> Email Quote
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => sendEmail('invoice')} disabled={!!actionLoading || !order.email}>
                <Mail className="w-4 h-4 mr-2" /> Email Invoice
              </DropdownMenuItem>
              {order.customer_id && (
                <DropdownMenuItem onClick={() => navigate(`/admin-portal?customer_id=${order.customer_id}`)}>
                  <ExternalLink className="w-4 h-4 mr-2" /> View in Portal
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                <Edit3 className="w-4 h-4" /> Status
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {['approved', 'in_production', 'on_hold', 'ready_for_pickup', 'completed', 'cancelled'].map(s => (
                <DropdownMenuItem key={s} onClick={() => updateOrderStatus(s)} disabled={order.status === s}>
                  {fmt(s)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Quick Photo — hidden inputs for camera and gallery */}
          <input ref={quickPhotoCameraRef} type="file" accept="image/*" capture="environment" onChange={handleQuickPhoto} className="hidden" data-testid="quick-photo-camera-input" />
          <input ref={quickPhotoGalleryRef} type="file" accept="image/*" onChange={handleQuickPhoto} className="hidden" data-testid="quick-photo-gallery-input" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1" disabled={quickPhotoUploading} data-testid="quick-photo-btn">
                {quickPhotoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Photo
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => triggerQuickPhoto('camera')} data-testid="quick-photo-camera">
                <Camera className="w-4 h-4 mr-2" /> Take Photo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => triggerQuickPhoto('gallery')} data-testid="quick-photo-gallery">
                <ImageIcon className="w-4 h-4 mr-2" /> Choose from Gallery
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={startProduction} disabled={!!actionLoading || workflowTickets.length === 0} data-testid="start-production-btn">
            {actionLoading === 'production' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />} Start Production
          </Button>
        </div>
      </div>

      {/* ─── Quick Actions Strip ─── */}
      <div className="flex flex-wrap gap-1.5 -mt-2" data-testid="order-quick-actions">
        {/* Photo */}
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => triggerQuickPhoto('camera')} disabled={quickPhotoUploading} data-testid="qa-take-photo">
          <Camera className="w-3.5 h-3.5" /> Take Photo
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => triggerQuickPhoto('gallery')} disabled={quickPhotoUploading} data-testid="qa-choose-photo">
          <ImageIcon className="w-3.5 h-3.5" /> Upload Photo
        </Button>
        {/* To-do / Calendar / Assign */}
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/productivity?view=list&order_id=${order.id}`)} data-testid="qa-add-todo">
          <ListTodo className="w-3.5 h-3.5" /> To-Do List
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/productivity?view=calendar&order_id=${order.id}`)} data-testid="qa-schedule">
          <CalendarPlus className="w-3.5 h-3.5" /> Schedule
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/productivity?view=kanban&order_id=${order.id}`)} data-testid="qa-assign">
          <UserPlus className="w-3.5 h-3.5" /> Assign
        </Button>
        {/* Quote */}
        {order.quote_id ? (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/quotes?id=${order.quote_id}`)} data-testid="qa-view-quote">
            <FileText className="w-3.5 h-3.5" /> View Quote
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => generateDoc('quote')} disabled={!!actionLoading || tickets.length === 0} data-testid="qa-create-quote">
            <FileText className="w-3.5 h-3.5" /> Create Quote
          </Button>
        )}
        {/* Invoice */}
        {order.invoice_id ? (
          <>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/invoices?id=${order.invoice_id}`)} data-testid="qa-view-invoice">
              <Receipt className="w-3.5 h-3.5" /> View Invoice
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => sendEmail('invoice')} disabled={!!actionLoading || !order.email} data-testid="qa-email-invoice">
              <Mail className="w-3.5 h-3.5" /> Email Invoice
            </Button>
            {order.customer_id && (
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/admin-portal?customer_id=${order.customer_id}`)} data-testid="qa-send-portal">
                <ExternalLink className="w-3.5 h-3.5" /> Send via Portal
              </Button>
            )}
          </>
        ) : (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => generateDoc('invoice')} disabled={!!actionLoading || tickets.length === 0} data-testid="qa-create-invoice">
            <Receipt className="w-3.5 h-3.5" /> Create Invoice
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: 'Items', value: tickets.length },
          { label: 'Estimate', value: `$${orderTotal.toFixed(2)}` },
          { label: 'Progress', value: `${Math.round(order.overall_progress || 0)}%` },
          { label: 'Due', value: order.requested_due_date ? new Date(order.requested_due_date).toLocaleDateString() : '-' },
          { label: 'Payment', value: fmt(order.payment_status) },
          { label: 'Source', value: fmt(order.order_source) },
        ].map(c => (
          <Card key={c.label} className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500 uppercase">{c.label}</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500" style={{ width: `${order.overall_progress || 0}%` }} />
      </div>

      {/* Tabs + Content Card */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex gap-1 border-b border-gray-200 overflow-x-auto px-4 pt-3">
          {[
            { id: 'tickets', label: `Order Items (${tickets.length})` },
            { id: 'production', label: `Production (${allTasks.length})` },
            { id: 'financial', label: `Financial (${(financials.quotes?.length || 0) + (financials.invoices?.length || 0) + (financials.work_orders?.length || 0)})` },
            { id: 'drawings', label: `Drawings (${drawings.length})` },
            { id: 'files', label: `Files (${orderFiles.length})` },
            { id: 'notes', label: 'Notes' },
            { id: 'activity', label: `Activity (${activities.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'text-violet-600 border-violet-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-4 lg:p-6">

      {/* === TICKETS TAB === */}
      {tab === 'tickets' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Order Items ({tickets.length})</h3>
            <AddOrderItemMenu
              orderId={id}
              existingItems={tickets.map((t) => ({ id: t.id, item_name: t.item_name, item_category: t.item_category, quantity: t.quantity, estimated_price: t.estimated_price }))}
              onQuickAdd={() => navigate(`/orders/${id}/add-ticket?mode=quick`)}
              onDetailedAdd={() => navigate(`/orders/${id}/add-ticket?mode=detailed`)}
              onCloneComplete={(newItem) => {
                toast.success(`Item created (${newItem?.clone_mode || 'cloned'})`);
                if (newItem?.id) navigate(`/job-tickets/${newItem.id}`);
                else window.location.reload();
              }}
              onAddWithSharedArtwork={(fileIds) => {
                const params = new URLSearchParams({ mode: 'detailed', shared_artwork: fileIds.join(',') });
                navigate(`/orders/${id}/add-ticket?${params.toString()}`);
              }}
            />
          </div>
          {tickets.length === 0 ? (
            <Card className="bg-white rounded-xl border border-gray-200 shadow-sm"><CardContent className="py-12 text-center text-gray-500">No order items yet.</CardContent></Card>
          ) : tickets.map(ticket => {
            const snapshot = ticket.pricing_snapshot;
            const pricingMode = snapshot?.pricing_mode || (ticket.estimated_price ? 'estimate' : 'none');
            const activePrice = snapshot?.active_price || ticket.estimated_price || 0;
            const specs = ticket.specs || {};
            const specSummary = [specs.width, specs.height, specs.material].filter(Boolean).join(' × ');
            const ticketDrawingCount = drawings.filter((drawing) => drawing.job_ticket_id === ticket.id).length;
            return (
              <Card key={ticket.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-violet-500/30 transition-colors cursor-pointer" onClick={() => navigate(`/job-tickets/${ticket.id}`)} data-testid={`ticket-${ticket.ticket_number}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${ticket.production_flow_enabled ? 'bg-violet-500/15' : 'bg-gray-200'}`}>
                        <Package className={`w-5 h-5 ${ticket.production_flow_enabled ? 'text-violet-400' : 'text-gray-500'}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm text-gray-500">{ticket.ticket_number}</span>
                          <Badge variant="outline" className={STATUS_COLORS[ticket.status]}>{fmt(ticket.status)}</Badge>
                          {ticket.priority !== 'normal' && <Badge className={PRIORITY_COLORS[ticket.priority]}>{ticket.priority}</Badge>}
                          {ticket.production_flow_enabled && <Badge variant="outline" className="bg-violet-500/10 text-violet-400 border-violet-500/30 text-xs">Workflow</Badge>}
                          {isWrapCategory(ticket.item_category) && (
                            <Badge
                              className="bg-violet-100 text-violet-800 border-violet-200 text-xs"
                              data-testid={`wrap-workflow-badge-${ticket.id}`}
                            >
                              Wrap Workflow
                            </Badge>
                          )}
                          {ticket.assigned_user_id && <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 text-xs">Assigned: {getEmployeeName(ticket.assigned_user_id)}</Badge>}
                          {/* Pricing mode badge */}
                          {pricingMode === 'calculator' && <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs"><Calculator className="w-3 h-3 mr-1" />Calc</Badge>}
                          {pricingMode === 'manual' && <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs"><Edit3 className="w-3 h-3 mr-1" />Manual</Badge>}
                          {ticket.design_needed && <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-xs">Design</Badge>}
                          {ticket.proof_required && <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 text-xs">Proof</Badge>}
                          {ticketDrawingCount > 0 && <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 text-xs">{ticketDrawingCount} drawing{ticketDrawingCount !== 1 ? 's' : ''}</Badge>}
                        </div>
                        <p className="text-gray-900 font-medium mt-0.5">{ticket.item_name}</p>
                        <p className="text-gray-500 text-xs">
                          {CATEGORY_LABELS[ticket.item_category] || ticket.item_category} | Qty: {ticket.quantity}
                          {specSummary ? ` | ${specSummary}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {isWrapCategory(ticket.item_category) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs bg-violet-50 border-violet-300 text-violet-800 hover:bg-violet-100 hidden sm:inline-flex"
                          onClick={(e) => { e.stopPropagation(); navigate(`/orders/${id}/items/${ticket.id}/wrap-command-center`); }}
                          data-testid={`open-wrap-cc-${ticket.id}`}
                        >
                          Open Wrap Command Center
                        </Button>
                      )}
                      {/* Price */}
                      <div className="text-right hidden sm:block">
                        <p className="text-lg font-bold text-gray-900">${activePrice.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{pricingMode !== 'none' ? pricingMode : 'no price'}</p>
                      </div>
                      {/* Progress */}
                      {ticket.production_flow_enabled && (
                        <div className="text-right hidden md:block">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-violet-500 rounded-full" style={{ width: `${ticket.progress || 0}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{Math.round(ticket.progress || 0)}%</span>
                          </div>
                        </div>
                      )}
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/job-tickets/${ticket.id}`); }}>
                            <Package className="w-4 h-4 mr-2" /> Open Item
                          </DropdownMenuItem>
                          {isWrapCategory(ticket.item_category) && (
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); navigate(`/orders/${id}/items/${ticket.id}/wrap-command-center`); }}
                              data-testid={`open-wrap-cc-menu-${ticket.id}`}
                            >
                              <Wrench className="w-4 h-4 mr-2" /> Open Wrap Command Center
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateTicket(ticket.id); }}>
                            <Copy className="w-4 h-4 mr-2" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/job-tickets/${ticket.id}?tab=drawings`); }} data-testid={`ticket-drawings-shortcut-${ticket.id}`}>
                            <Pen className="w-4 h-4 mr-2" /> Open Drawings
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); triggerQuickPhoto('camera', ticket.id); }} data-testid={`ticket-quick-photo-${ticket.id}`}>
                            <Camera className="w-4 h-4 mr-2" /> Quick Photo
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShortcutTicket(ticket); setShortcutMode('assign'); }} data-testid={`ticket-assign-shortcut-${ticket.id}`}>
                            <UserPlus className="w-4 h-4 mr-2" /> Assign Employee
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShortcutTicket(ticket); setShortcutMode('schedule'); }} data-testid={`ticket-schedule-shortcut-${ticket.id}`}>
                            <CalendarPlus className="w-4 h-4 mr-2" /> Add to Schedule
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShortcutTicket(ticket); setShortcutMode('task'); }} data-testid={`ticket-task-shortcut-${ticket.id}`}>
                            <ListTodo className="w-4 h-4 mr-2" /> Create Task
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-400" onClick={(e) => deleteTicket(ticket.id, e)}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* === PRODUCTION TAB === */}
      {tab === 'production' && (
        <div className="space-y-4">
          {prodSummary?.summary && (
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-white rounded-xl border border-gray-200 shadow-sm"><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Total Tasks</p><p className="text-xl font-bold text-gray-900">{prodSummary.summary.total_tasks}</p></CardContent></Card>
              <Card className="bg-white rounded-xl border border-gray-200 shadow-sm"><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Completed</p><p className="text-xl font-bold text-green-400">{prodSummary.summary.completed}</p></CardContent></Card>
              <Card className="bg-white rounded-xl border border-gray-200 shadow-sm"><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Flagged</p><p className="text-xl font-bold text-red-400">{prodSummary.summary.on_hold}</p></CardContent></Card>
            </div>
          )}
          {allTasks.length === 0 ? (
            <Card className="bg-white rounded-xl border border-gray-200 shadow-sm"><CardContent className="py-12 text-center text-gray-500">No production tasks. Start production to generate tasks.</CardContent></Card>
          ) : (
            Object.entries(prodSummary?.by_department || {}).map(([dept, tasks]) => (
              <div key={dept}>
                <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">{DEPT_LABELS[dept] || fmt(dept)} ({tasks.length})</h3>
                <div className="space-y-1">
                  {tasks.map(task => {
                    const ticket = (prodSummary?.tickets || []).find(t => t.id === task.job_ticket_id);
                    return (
                      <Card key={task.id} className={`bg-white border-gray-200 ${task.status === 'in_progress' ? 'border-l-4 border-l-violet-500' : task.status === 'complete' ? 'border-l-4 border-l-green-500' : ''}`}>
                        <CardContent className="p-3 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-900 text-sm font-medium">{task.task_name}</span>
                              <Badge variant="outline" className={`text-xs ${STATUS_COLORS[task.status]}`}>{fmt(task.status)}</Badge>
                            </div>
                            <p className="text-xs text-gray-500">{ticket?.ticket_number} — {ticket?.item_name}</p>
                          </div>
                          <div className="flex gap-1">
                            {task.status !== 'complete' && task.status !== 'in_progress' && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-violet-400" onClick={() => updateTask(task.id, 'in_progress')} disabled={taskLoading === task.id}>
                                {taskLoading === task.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                              </Button>
                            )}
                            {task.status === 'in_progress' && (
                              <>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-400" onClick={() => updateTask(task.id, 'complete')} disabled={taskLoading === task.id}><CheckCircle className="w-3 h-3" /></Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-orange-400" onClick={() => updateTask(task.id, 'paused')} disabled={taskLoading === task.id}><Pause className="w-3 h-3" /></Button>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* === FINANCIAL TAB === */}
      {tab === 'financial' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => generateDoc('quote')} disabled={!!actionLoading || tickets.length === 0}>
              {actionLoading === 'quote' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />} Generate Quote
            </Button>
            <Button variant="outline" size="sm" onClick={() => generateDoc('invoice')} disabled={!!actionLoading || tickets.length === 0}>
              {actionLoading === 'invoice' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Receipt className="w-4 h-4 mr-1" />} Generate Invoice
            </Button>
          </div>
          {financials.quotes?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Quotes ({financials.quotes.length})</h3>
              {financials.quotes.map(q => (
                <Card key={q.id} className="bg-white border-gray-200 mb-2">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-purple-400" /><span className="text-gray-900 font-medium">Quote</span><Badge variant="outline" className={STATUS_COLORS[q.status]}>{fmt(q.status)}</Badge></div>
                        <p className="text-xs text-gray-500 mt-1">{new Date(q.created_at).toLocaleDateString()} | {q.line_items?.length || 0} items</p>
                      </div>
                      <p className="text-xl font-bold text-gray-900">${(q.total || 0).toFixed(2)}</p>
                    </div>
                    <div className="mt-4">
                      <SignatureSection
                        compact
                        title="Quote Signature"
                        parentRecordType="quote"
                        parentRecordId={q.id}
                        orderId={order.id}
                        signatureType="quote_acceptance"
                        documentVersion={String(q.created_at || '')}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {financials.invoices?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Invoices ({financials.invoices.length})</h3>
              {financials.invoices.map(inv => (
                <Card key={inv.id} className="bg-white border-gray-200 mb-2">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2"><Receipt className="w-4 h-4 text-green-400" /><span className="text-gray-900 font-medium">Invoice</span><Badge variant="outline" className={STATUS_COLORS[inv.status]}>{fmt(inv.status)}</Badge></div>
                        <p className="text-xs text-gray-500 mt-1">{new Date(inv.created_at).toLocaleDateString()} | {inv.line_items?.length || 0} items{inv.due_date ? ` | Due: ${new Date(inv.due_date).toLocaleDateString()}` : ''}</p>
                      </div>
                      <p className="text-xl font-bold text-gray-900">${(inv.total || 0).toFixed(2)}</p>
                    </div>
                    <div className="mt-4">
                      <SignatureSection
                        compact
                        title="Invoice Signature"
                        parentRecordType="invoice"
                        parentRecordId={inv.id}
                        orderId={order.id}
                        signatureType="payment_authorization"
                        documentVersion={String(inv.created_at || '')}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {financials.work_orders?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Work Orders ({financials.work_orders.length})</h3>
              {financials.work_orders.map((workOrder) => (
                <Card key={workOrder.id} className="bg-white border-gray-200 mb-2" data-testid={`work-order-card-${workOrder.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-500" /><span className="text-gray-900 font-medium">Work Order</span><Badge variant="outline" className={STATUS_COLORS[workOrder.status] || STATUS_COLORS.draft}>{fmt(workOrder.status)}</Badge></div>
                        <p className="text-xs text-gray-500 mt-1">{new Date(workOrder.created_at).toLocaleDateString()} | {workOrder.total_tickets || 0} tickets</p>
                      </div>
                      <p className="text-sm font-medium text-gray-900">{workOrder.order_number || order.order_number}</p>
                    </div>
                    <div className="mt-4">
                      <SignatureSection
                        compact
                        title="Work Order Signature"
                        parentRecordType="work_order"
                        parentRecordId={workOrder.id}
                        orderId={order.id}
                        signatureType="order_authorization"
                        documentVersion={String(workOrder.created_at || '')}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {(financials.quotes?.length || 0) === 0 && (financials.invoices?.length || 0) === 0 && (financials.work_orders?.length || 0) === 0 && (
            <Card className="bg-white rounded-xl border border-gray-200 shadow-sm"><CardContent className="py-12 text-center text-gray-500">No documents yet. Generate a quote, invoice, or work order from the order items.</CardContent></Card>
          )}
        </div>
      )}


      {/* === DRAWINGS TAB === */}
      {tab === 'drawings' && (
        <div className="space-y-4" data-testid="drawings-tab">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-gray-500">{drawings.length} drawing{drawings.length !== 1 ? 's' : ''} across the order, items, and markup images</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {drawingFilterOptions.map((option) => (
                  <Button key={option.id} variant={drawingFilter === option.id ? 'default' : 'outline'} size="sm" onClick={() => setDrawingFilter(option.id)} data-testid={`drawing-filter-${option.id}`}>
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white gap-1" onClick={() => { setDrawingMarkupImage(null); setShowDrawingModal(true); }} data-testid="add-drawing-btn">
              <Pen className="w-4 h-4" /> {latestOrderDraftDrawing ? 'Resume Order Draft' : 'Add Order Drawing'}
            </Button>
          </div>
          {filteredDrawings.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Pen className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium text-gray-500">No drawings yet</p>
              <p className="text-sm mt-1">Add order sketches, item notes, or image markups to this order</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredDrawings.map(d => {
                const relatedTicket = tickets.find((ticket) => ticket.id === d.job_ticket_id);
                const contextLabel = d.parent_type === 'job_ticket'
                  ? `Item Drawing: ${relatedTicket?.item_name || 'Item'}`
                  : d.parent_type === 'uploaded_image'
                    ? `Image Markup: ${d.label}`
                    : 'Order Drawing';
                return (
                <div
                  key={d.id}
                  className="border border-gray-200 rounded-xl overflow-hidden hover:border-violet-400 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => setPreviewDrawing(d)}
                  data-testid={`drawing-thumb-${d.id}`}
                >
                  <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center overflow-hidden">
                    {drawingThumbs[d.id] ? (
                      <img src={drawingThumbs[d.id]} alt={d.label} className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.label}</p>
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">{contextLabel}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        d.type === 'sketch' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{d.type}</span>
                      <span className="text-[10px] text-gray-400">{new Date(d.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">{d.created_by}</p>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>
      )}

      {/* === FILES TAB === */}
      {tab === 'files' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{orderFiles.length} file{orderFiles.length !== 1 ? 's' : ''} attached</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  fetchTemplates();
                  setShowTemplateSelector(true);
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Use Template
              </Button>
              <input type="file" multiple onChange={handleFileUpload} className="hidden" id="order-detail-file-input" />
              <label htmlFor="order-detail-file-input">
                <Button asChild variant="outline" size="sm" className="gap-2 cursor-pointer" disabled={uploadingFile}>
                  <span>{uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Files</span>
                </Button>
              </label>
            </div>
          </div>
          {orderFiles.length === 0 ? (
            <Card className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <CardContent className="py-12 text-center">
                <Paperclip className="w-8 h-8 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No files attached to this order</p>
                <p className="text-gray-400 text-sm mt-1">Upload artwork, drawings, photos, or any reference files</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {orderFiles.map(f => (
                <Card key={f.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-14 h-14 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-violet-100" data-testid={`order-file-thumbnail-${f.id}`}>
                        {f.content_type?.startsWith('image/') && orderFileThumbs[f.id] ? (
                          <img src={orderFileThumbs[f.id]} alt={f.label || f.filename} className="w-full h-full object-cover" />
                        ) : (
                          <FileUp className="w-5 h-5 text-violet-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{f.label || f.filename}</p>
                        <p className="text-xs text-gray-500">{f.filename} | {(f.file_size / 1024).toFixed(0)} KB | {new Date(f.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {f.content_type?.startsWith('image/') && (
                        <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-600" onClick={() => {
                          getFileContentUrl(f).then((contentUrl) => {
                            setDrawingMarkupImage({ id: f.id, label: f.label || f.filename, contentUrl });
                            setShowDrawingModal(true);
                          });
                        }} data-testid={`markup-order-file-${f.id}`}>
                          <Pen className="w-4 h-4" />
                        </Button>
                      )}
                      {f.content_type?.startsWith('image/') && (
                        <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-600" onClick={() => getFileContentUrl(f).then((contentUrl) => setPreviewFile({ label: f.label || f.filename, contentUrl }))} data-testid={`preview-order-file-${f.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {f.content_type?.startsWith('image/') && order.customer_id && (
                        <Button variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-600" onClick={() => sendFileForApproval(f)} data-testid={`approve-order-file-${f.id}`}>
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => deleteFile(f.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* === NOTES TAB === */}
      {tab === 'notes' && (
        <div className="space-y-4">
          {/* Shared order-level context (inherited by every item) */}
          <SharedContextPanel
            order={order}
            onChange={async (patch) => {
              try {
                const token = getAuthToken();
                const diff = {};
                ['order_title','shared_production_notes','shared_design_notes','shared_install_notes','shared_color_brand_notes','requested_due_date'].forEach((k) => {
                  if (patch[k] !== order[k]) diff[k] = patch[k];
                });
                if (Object.keys(diff).length) {
                  await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}`, diff, { headers: { Authorization: `Bearer ${token}` } });
                  setOrder((prev) => ({ ...prev, ...diff }));
                  toast.success('Shared context saved');
                }
              } catch (err) {
                toast.error('Save failed');
              }
            }}
          />
          <OrderAssetsPanel orderId={id} />
          <SignatureSection
            title="Order Authorization Signature"
            parentRecordType="order"
            parentRecordId={order.id}
            orderId={order.id}
            signatureType="order_authorization"
            documentVersion={String(order.updated_at || order.date_created || '')}
          />
          <SignatureSection
            title="Change Approval Signature"
            parentRecordType="change_order"
            parentRecordId={order.id}
            orderId={order.id}
            signatureType="change_approval"
            documentVersion={String(order.updated_at || order.date_created || '')}
          />
          <SignatureSection
            title={order.pickup_delivery_method === 'install' ? 'Install Completion Signature' : order.pickup_delivery_method === 'delivery' ? 'Delivery Confirmation Signature' : 'Pickup Confirmation Signature'}
            parentRecordType={order.pickup_delivery_method === 'install' ? 'install_record' : order.pickup_delivery_method === 'delivery' ? 'delivery_record' : 'pickup_record'}
            parentRecordId={order.id}
            orderId={order.id}
            signatureType={order.pickup_delivery_method === 'install' ? 'install_completion' : order.pickup_delivery_method === 'delivery' ? 'delivery_confirmation' : 'pickup_confirmation'}
            documentVersion={String(order.updated_at || order.date_created || '')}
          />
          <SignatureActivityList orderId={order.id} />
          <Card className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-gray-500" /><p className="text-sm font-medium text-gray-700">Internal Notes</p></div>
              <p className="text-gray-900 text-sm">{order.internal_notes || <span className="text-gray-400 italic">No internal notes</span>}</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-gray-500" /><p className="text-sm font-medium text-gray-700">Customer Notes</p></div>
              <p className="text-gray-900 text-sm">{order.customer_notes || <span className="text-gray-400 italic">No customer notes</span>}</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2"><Package className="w-4 h-4 text-gray-500" /><p className="text-sm font-medium text-gray-700">Pickup / Delivery</p></div>
              <p className="text-gray-900 text-sm">{fmt(order.pickup_delivery_method)}{order.pickup_delivery_notes ? ` — ${order.pickup_delivery_notes}` : ''}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* === ACTIVITY TAB === */}
      {tab === 'activity' && (
        <div className="space-y-2">
          {activities.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No activity yet</p>
          ) : activities.map(a => (
            <div key={a.id} className="flex items-start gap-3 py-2 border-b border-gray-200 last:border-0">
              <div className="w-2 h-2 rounded-full bg-violet-500 mt-2 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-900">{a.description}</p>
                <p className="text-xs text-gray-500 mt-0.5">{new Date(a.created_at).toLocaleString()}{a.user_name ? ` by ${a.user_name}` : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pt-4 border-t border-gray-200">
        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={deleteOrder}>
          <Trash2 className="w-4 h-4 mr-2" /> Delete Order
        </Button>
      </div>
        </div>
      </Card>

      {/* Drawing Modals */}
      {showDrawingModal && (
        <DrawingModal
          orderId={id}
          parentType={drawingMarkupImage ? 'uploaded_image' : 'order'}
          parentId={drawingMarkupImage ? drawingMarkupImage.id : id}
          uploadedImage={drawingMarkupImage}
          existingDrawing={!drawingMarkupImage ? latestOrderDraftDrawing : null}
          defaultType={drawingMarkupImage ? 'markup' : 'sketch'}
          onClose={() => {
            setShowDrawingModal(false);
            setDrawingMarkupImage(null);
          }}
          onSaved={load}
        />
      )}
      {previewDrawing && (
        <DrawingPreviewModal
          drawing={previewDrawing}
          onClose={() => setPreviewDrawing(null)}
          onDeleted={load}
          isAdmin={true}
        />
      )}
      <TicketWorkflowShortcutDialog
        open={!!shortcutMode && !!shortcutTicket}
        mode={shortcutMode}
        ticket={shortcutTicket}
        order={order}
        employees={employees}
        onClose={() => {
          setShortcutMode('');
          setShortcutTicket(null);
        }}
        onCompleted={load}
      />
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="sm:max-w-[760px]">
          <DialogHeader><DialogTitle>{previewFile?.label || 'Artwork Preview'}</DialogTitle></DialogHeader>
          {previewFile?.contentUrl && <img src={previewFile.contentUrl} alt={previewFile.label} className="w-full max-h-[70vh] object-contain rounded-lg" />}
        </DialogContent>
      </Dialog>
      
      {/* Template Selector Dialog */}
      <Dialog open={showTemplateSelector} onOpenChange={setShowTemplateSelector}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Use Document Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a template to create a document for this order. All variables will be automatically filled.
            </p>
            
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">No templates available</p>
              </div>
            ) : (
              <div className="grid gap-2 max-h-[400px] overflow-y-auto">
                {templates.map(template => (
                  <Card 
                    key={template.id}
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => handleUseTemplate(template)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="h-4 w-4 text-primary" />
                            <h4 className="font-medium">{template.name}</h4>
                          </div>
                          {template.description && (
                            <p className="text-sm text-muted-foreground text-xs">{template.description}</p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
